import { useState, useEffect, useContext, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import UIContext from "../../contexts/ui/UIContext";
import PlantTaskCard from "./PlantTaskCard.jsx";
import PlantTaskFilter from "./PlantTaskFilter.jsx";
import Loading from "../ui-elements/Loading.jsx";
import { plantTaskConfig, filterOptions } from "../../constants/plantTaskMappings.js";
import useFilteredPlantTasks from "../../customHooks/useFilteredPlantTasks.js";
import TasksApiService from '../../services/apiServices/TasksApiService.jsx';
import AuthContext from "../../contexts/auth/AuthContext.jsx";
import ProcessContext from "../../contexts/process/ProcessContext.js";
import { HELP_DESK_LINK } from "../../constants/helpDesk.jsx";
import { useNavigate } from "react-router-dom";

const PlantTaskList = () => {
    const { setInfo } = useContext(UIContext);
    const { currentContainer } = useContext(AuthContext);
    const { notifyError } = useContext(ProcessContext);
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('');
    const [plantTasks, setPlantTasks] = useState(null);

    const scrollRef = useRef(null);
    const filteredPlantTasks = useFilteredPlantTasks(plantTasks, activeFilter);
    const hasPlantTasks = Array.isArray(filteredPlantTasks) && filteredPlantTasks.length > 0;

    useEffect(() => {
        const controller = new AbortController();
        loadPlantTasks(controller.signal);

        return () => {
          controller.abort();
          setPlantTasks(null)
        };
    }, [currentContainer]);


    useEffect(() => {
        setInfo("Plant Task");
    }, []);

    const getScrollContainer = () => {
        const candidates = [
          scrollRef.current,
          document.getElementById("layout-container-safari-body"),
          document.getElementById("layout-container-body"),
          document.scrollingElement,
          document.documentElement,
          document.body,
        ].filter(Boolean);
        return candidates.find((el) => el.scrollHeight > el.clientHeight + 1) || candidates[0];
      };

    const resetScroll = () => {
        const el = getScrollContainer();
        try { el.scrollTop = 0; } catch {
            // Ignore unsupported scroll containers.
        }
        try { window.scrollTo({ top: 0, behavior: "auto" }); } catch {
            // Ignore browsers that do not support programmatic scrolling.
        }
        };

        useLayoutEffect(() => {
            requestAnimationFrame(() => {
            resetScroll();
            requestAnimationFrame(resetScroll);
            });
            }, [activeFilter, currentContainer, plantTasks?.length]);    

    const loadPlantTasks = async (signal) => {
        let loadedPlantTasks = [];
        if(!currentContainer) {
            navigate('/');
            return;
        }
        try {
            const responseFromTasksApi = await TasksApiService.getTasksAsync(currentContainer?.tenantId, currentContainer?.id, signal);
            loadedPlantTasks = responseFromTasksApi.data.data;
            setPlantTasks(loadedPlantTasks);
        } catch (err) {
            if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
              return;
            }

            console.warn("Unexpected error while loading plant tasks. ", err);
            notifyError(<>We have encountered an issue on our side while loading the plant tasks for this container, please contact the support team {HELP_DESK_LINK}.</>);
            setPlantTasks([]);
          }
    }

    const changeFilter = (option) => {
        setActiveFilter(option);
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
            }
    };

    const handleTaskClick = (task) => {
        navigate(`/processes/start?taskId=${task.id}`);
        };

        return (
        <>
              {plantTasks !== null ? (
                <div
                  className="plant-task-list"
                  ref={scrollRef}
                  key={`${currentContainer?.id || "default"}-${activeFilter}`}
                >
                  <PlantTaskFilter
                    activeFilter={activeFilter}
                    filterOptions={filterOptions}
                    plantTaskConfig={plantTaskConfig}
                    changeFilter={changeFilter}
                  />
          
                  {hasPlantTasks ? (
                    <div className="cards-container">
                      {filteredPlantTasks.map((plantTask) => (
                        <PlantTaskCard
                          key={plantTask.id}
                          plantTask={plantTask}
                          onTaskClick={handleTaskClick}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="no-plant-tasks-container">
                      <div className="no-plant-task">
                        <p>No Plant Tasks.</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="plant-task-list">
                <Loading fullScreen={true} />
              </div>
              )}
            </>
          );
          };


const panelShape = PropTypes.shape({
    name: PropTypes.string.isRequired,
});

const trayShape = PropTypes.shape({
    name: PropTypes.string.isRequired,
});

PlantTaskList.propTypes = {
    plantTasks: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            processType: PropTypes.string.isRequired,
            panel: PropTypes.oneOfType([panelShape, PropTypes.oneOf([null])]),
            tray: PropTypes.oneOfType([trayShape, PropTypes.oneOf([null])]),
        })
    ),
    defaultFilter: PropTypes.string,
    onTaskClick: PropTypes.func,
};

export default PlantTaskList;
